"use client";

import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { TimezoneCombobox } from "./timezone-combobox";
import type { WorkingHoursConfig, DaySchedule } from "@/lib/calendar";

const DAY_LABELS = {
  monday: "Monday",
  tuesday: "Tuesday",
  wednesday: "Wednesday",
  thursday: "Thursday",
  friday: "Friday",
  saturday: "Saturday",
  sunday: "Sunday",
};

type DayKey = keyof typeof DAY_LABELS;

interface WorkingHoursConfigProps {
  value: WorkingHoursConfig;
  onChange: (config: WorkingHoursConfig) => void;
  disabled?: boolean;
}

export function WorkingHoursConfigEditor({
  value,
  onChange,
  disabled,
}: WorkingHoursConfigProps) {
  const handleTimezoneChange = (timezone: string) => {
    onChange({ ...value, timezone });
  };

  const handleDayChange = (day: DayKey, updates: Partial<DaySchedule>) => {
    onChange({
      ...value,
      schedule: {
        ...value.schedule,
        [day]: {
          ...value.schedule[day],
          ...updates,
        },
      },
    });
  };

  return (
    <div className="space-y-4">
      {/* Timezone */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">Timezone</Label>
        <TimezoneCombobox
          value={value.timezone}
          onChange={handleTimezoneChange}
          disabled={disabled}
        />
      </div>

      {/* Weekly Schedule */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">Weekly Schedule</Label>
        <div className="space-y-2">
          {(Object.keys(DAY_LABELS) as DayKey[]).map((day) => {
            const daySchedule = value.schedule[day];
            return (
              <div
                key={day}
                className="flex items-center gap-3 p-2 border rounded-lg bg-gray-50"
              >
                <Switch
                  checked={daySchedule.enabled}
                  onCheckedChange={(enabled) =>
                    handleDayChange(day, { enabled })
                  }
                  disabled={disabled}
                  className="scale-90"
                />
                <span className="w-24 text-sm font-medium text-gray-700">
                  {DAY_LABELS[day]}
                </span>
                {daySchedule.enabled ? (
                  <div className="flex items-center gap-2">
                    <Input
                      type="time"
                      value={daySchedule.start}
                      onChange={(e) =>
                        handleDayChange(day, { start: e.target.value })
                      }
                      disabled={disabled}
                      className="w-28 text-sm"
                    />
                    <span className="text-gray-400">-</span>
                    <Input
                      type="time"
                      value={daySchedule.end}
                      onChange={(e) =>
                        handleDayChange(day, { end: e.target.value })
                      }
                      disabled={disabled}
                      className="w-28 text-sm"
                    />
                  </div>
                ) : (
                  <span className="text-sm text-gray-400">Unavailable</span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
